import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { CommonFormElementSize, CommonSize } from '@/common/types'
import { LabeledChoiceElement } from '@/internal/mixin/labeledChoiceElement'

import styles from './_styles/checkbox.scss?inline'

/**
 * `dap-ds-checkbox`
 * @summary A checkbox is a form element that allows the user to select one or more options from a set.
 * @element dap-ds-checkbox
 * @title - Checkbox
 *
 * @event {{ checked: boolean, indeterminate: boolean, value: string, disabled: boolean, type: 'checkbox' }} dds-change - Fired when the checkbox is checked or unchecked.
 * @event {{ void }} dds-blur - Emitted when the checkbox loses focus.
 * @event {{ void }} dds-focus - Emitted when the checkbox gains focus.
 * @event {{ checked: boolean, indeterminate: boolean, value: string, disabled: boolean }} dds-input - Emitted when the checkbox receives input.
 *
 * @property {string} name - The name of the checkbox.
 * @property {string} value - The value of the checkbox.
 * @property {boolean} checked - Whether the checkbox is checked.
 * @property {string} label - The label of the checkbox.
 * @property {string} description - The description of the checkbox.
 * @property {boolean} disabled - Whether the checkbox is disabled.
 * @property {boolean} required - Whether the checkbox is required.
 * @property {'xs' | 'sm' | 'md' | 'lg'} size - The size of the checkbox.
 * @property {'left' | 'right'} labelPlacement - The placement of the label.
 * @property {'top' | 'bottom'} descriptionPlacement - The placement of the description.
 * @property {boolean} subtle - The weight of the label.
 * @property {string} feedback - The feedback of the checkbox.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the checkbox.
 * @property {boolean} invalid - The invalid state of the checkbox.
 * @property {boolean} optional - The optional state of the checkbox.
 * @property {string} optionalLabel - The optional label of the checkbox.
 *
 * @csspart base - The main checkbox container.
 * @csspart label - The label of the checkbox.
 * @csspart input - The input of the checkbox.
 * @csspart control - The control of the checkbox.
 * @csspart icon - The icon of the checkbox.
 * @csspart label-container - The label container of the checkbox.
 * @csspart description - The description of the checkbox.
 *
 * @cssproperty --dds-checkbox-size - The size of the checkbox. Default is `var(--dds-spacing-500)`.
 * @cssproperty --dds-checkbox-border-width - The border width of the checkbox. Default is `var(--dds-border-width-large)`.
 * @cssproperty --dds-checkbox-border-radius - The border radius of the checkbox. Default is `var(--dds-radius-small)`.
 * @cssproperty --dds-checkbox-border-color - The border color of the checkbox. Default is `var(--dds-border-neutral-base)`.
 * @cssproperty --dds-checkbox-background-color - The background color of the checkbox. Default is `transparent`.
 * @cssproperty --dds-checkbox-icon-color - The color of the checkbox icon. Default is `var(--dds-button-primary-icon-enabled)`.
 * @cssproperty --dds-checkbox-hover-border-color - The border color when hovering over the checkbox. Default is `var(--dds-border-neutral-medium)`.
 * @cssproperty --dds-checkbox-hover-background-color - The background color when hovering over the checkbox. Default is `var(--dds-background-neutral-medium)`.
 * @cssproperty --dds-checkbox-active-border-color - The border color when the checkbox is active. Default is `var(--dds-border-neutral-strong)`.
 * @cssproperty --dds-checkbox-active-background-color - The background color when the checkbox is active. Default is `var(--dds-background-neutral-strong)`.
 * @cssproperty --dds-checkbox-checked-border-color - The border color when the checkbox is checked. Default is `var(--dds-background-brand-base-inverted)`.
 * @cssproperty --dds-checkbox-checked-background-color - The background color when the checkbox is checked. Default is `var(--dds-background-brand-base-inverted)`.
 * @cssproperty --dds-checkbox-checked-hover-border-color - The border color when hovering over a checked checkbox. Default is `var(--dds-background-brand-medium-inverted)`.
 * @cssproperty --dds-checkbox-checked-hover-background-color - The background color when hovering over a checked checkbox. Default is `var(--dds-background-brand-medium-inverted)`.
 * @cssproperty --dds-checkbox-checked-active-border-color - The border color when a checked checkbox is active. Default is `var(--dds-background-brand-strong-inverted)`.
 * @cssproperty --dds-checkbox-checked-active-background-color - The background color when a checked checkbox is active. Default is `var(--dds-background-brand-strong-inverted)`.
 * @cssproperty --dds-checkbox-invalid-border-color - The border color when the checkbox is invalid. Default is `var(--dds-border-negative-base)`.
 * @cssproperty --dds-checkbox-invalid-background-color - The background color when the checkbox is invalid. Default is `var(--dds-background-negative-base)`.
 * @cssproperty --dds-checkbox-invalid-hover-border-color - The border color when hovering over an invalid checkbox. Default is `var(--dds-border-negative-medium)`.
 * @cssproperty --dds-checkbox-invalid-hover-background-color - The background color when hovering over an invalid checkbox. Default is `var(--dds-background-negative-medium)`.
 * @cssproperty --dds-checkbox-invalid-active-border-color - The border color when an invalid checkbox is active. Default is `var(--dds-border-negative-strong)`.
 * @cssproperty --dds-checkbox-invalid-active-background-color - The background color when an invalid checkbox is active. Default is `var(--dds-background-negative-strong)`.
 * @cssproperty --dds-checkbox-disabled-border-color - The border color when the checkbox is disabled. Default is `var(--dds-button-primary-background-disabled)`.
 * @cssproperty --dds-checkbox-disabled-background-color - The background color when the checkbox is disabled. Default is `var(--dds-button-primary-background-disabled)`.
 * @cssproperty --dds-checkbox-disabled-icon-color - The color of the checkbox icon when disabled. Default is `var(--dds-button-primary-icon-disabled)`.
 */
@customElement('dap-ds-checkbox')
export default class DapDSCheckbox extends LabeledChoiceElement {
  @query('#input') private readonly input!: HTMLInputElement

  /** Whether the checkbox is indeterminate */
  @property({ type: Boolean, reflect: true }) indeterminate = false

  /** Whether the checkbox should prevent the default action */
  @property({ type: Boolean, reflect: true }) preventDefault: boolean = false

  /** This sets up border around the checkbox, when true. */
  @property({ type: Boolean }) border: boolean = false

  /** The type of the checkbox
   * @type {'normal' | 'background'}
   */
  @property({ type: String }) type: 'normal' | 'background' = 'normal'

  static readonly styles = unsafeCSS(styles)

  /**
   * Validates property combinations and logs warnings for invalid states.
   * @private
   */
  private validateProperties(): void {
    // Warn if both checked and indeterminate are true (invalid combination)
    if (this.checked && this.indeterminate) {
      console.warn(
        'Checkbox: Cannot be both checked and indeterminate simultaneously. Indeterminate state will take precedence.',
      )
    }
  }

  /**
   * Gets the aria-describedby attribute value by combining description and feedback IDs.
   * @returns {string | undefined} Space-separated IDs or undefined if none exist
   * @private
   */
  private getAriaDescribedBy(): string | undefined {
    const ids: string[] = []

    if (this.description) {
      ids.push('description')
    }

    if (this.feedback) {
      ids.push('feedback')
    }

    return ids.length > 0 ? ids.join(' ') : undefined
  }

  /**
   * Gets the aria-checked attribute value based on checkbox state.
   * @returns {'true' | 'false' | 'mixed'} The appropriate aria-checked value
   * @private
   */
  private getAriaChecked(): 'true' | 'false' | 'mixed' {
    if (this.indeterminate) {
      return 'mixed'
    }
    return this.checked ? 'true' : 'false'
  }

  /**
   * Handles click events on the checkbox.
   * Manages the checked/indeterminate state transitions and emits change events.
   * @private
   */
  private handleClick() {
    // Early return if click should be prevented or component is disabled
    if (this.preventDefault || this.disabled) {
      return
    }

    // Validate property combinations before state change
    this.validateProperties()

    // Handle state transitions
    if (this.indeterminate) {
      this.indeterminate = false
      this.checked = true
    } else {
      this.checked = !this.checked
    }

    const { checked, indeterminate, value, disabled } = this

    // Emit change event with null safety
    try {
      this.emit('dds-change', {
        checked,
        indeterminate,
        value: value || (checked ? 'on' : 'off'),
        disabled,
        type: 'checkbox',
      })
    } catch (error) {
      console.error('Error emitting checkbox change event:', error)
    }

    // Update validity state with error handling
    try {
      this.setValidity()
    } catch (error) {
      console.error('Error setting checkbox validity:', error)
    }
  }

  /**
   * Handles input events on the checkbox.
   * Emits the dds-input event with current checkbox state.
   * @private
   */
  private handleInput() {
    const { checked, indeterminate, value, disabled } = this

    const event = new CustomEvent('dds-input', {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: { checked, indeterminate, value, disabled },
    })

    this.dispatchEvent(event)
  }

  /**
   * Handles keyboard events on the checkbox.
   * Provides enhanced keyboard navigation and accessibility.
   * @param {KeyboardEvent} event - The keyboard event
   * @private
   */
  private handleKeyDown(event: KeyboardEvent): void {
    // Handle Space key to toggle checkbox (in addition to default behavior)
    if (event.code === 'Space') {
      // Allow default Space behavior for checkbox input
      // This ensures proper accessibility and form behavior
      return
    }

    // Handle Enter key to toggle checkbox (custom behavior)
    if (event.code === 'Enter') {
      event.preventDefault()
      this.handleClick()
    }
  }

  public get focusElement(): HTMLInputElement {
    if (!this.input) {
      throw new Error(
        'Checkbox input element not found. Component may not be properly initialized.',
      )
    }
    return this.input
  }

  /**
   * Programmatically focuses the checkbox input element.
   * @public
   */
  public focus(): void {
    if (this.input && !this.disabled) {
      this.input.focus()
    }
  }

  /**
   * Programmatically blurs the checkbox input element.
   * @public
   */
  public blur(): void {
    if (this.input) {
      this.input.blur()
    }
  }

  /**
   * Renders the appropriate icon based on checkbox state.
   * @returns {TemplateResult | null} The icon template or null if unchecked
   * @private
   */
  renderIcon() {
    if (this.indeterminate) {
      return html`
        <dap-ds-icon-subtract-line
          class="checkbox__icon"
          part="icon"
          size="16"
          decorative></dap-ds-icon-subtract-line>
      `
    }

    if (this.checked) {
      return html`
        <dap-ds-icon-check-line2
          class="checkbox__icon"
          part="icon"
          size="16"
          decorative></dap-ds-icon-check-line2>
      `
    }

    return null
  }

  /**
   * Handles invalid events on the checkbox input.
   * Prevents the default browser validation UI from showing and manages invalid state.
   * @param {Event} event - The invalid event
   * @private
   */
  handleInvalid(event: Event) {
    event.preventDefault()
    // Set invalid state if not already set
    if (!this.invalid) {
      this.invalid = true
    }
  }

  render() {
    return html`
      <div part="base">
        <label
          part="base-label"
          id="label"
          class=${clsx('checkbox choice', `checkbox--${this.size}`, {
            'checkbox--checked': this.checked,
            'checkbox--disabled': this.disabled,
            'checkbox--indeterminate': this.indeterminate,
            'checkbox--required': this.required,
            'choice__label--right': this.labelPlacement === 'right',
            'choice__label--left': this.labelPlacement === 'left',
          })}>
          <div
            part="wrapper"
            class=${clsx('input__wrapper', {
              'input__wrapper--background': this.type === 'background',
              'input__wrapper--border': this.border,
            })}>
            <input
              part="input"
              type="checkbox"
              class=${clsx('input', {
                'input--background': this.type === 'background',
              })}
              name=${ifDefined(this.name ? this.name : undefined)}
              id="input"
              .indeterminate=${this.indeterminate}
              .checked=${this.checked}
              .disabled=${this.disabled}
              .required=${this.required}
              aria-label=${ifDefined(this.ariaLabel) || ''}
              aria-describedby=${ifDefined(this.getAriaDescribedBy())}
              aria-checked=${this.getAriaChecked()}
              .aria-invalid=${this.invalid ? 'true' : null}
              @invalid=${this.handleInvalid}
              @click=${this.handleClick}
              @input=${this.handleInput}
              @keydown=${this.handleKeyDown}
              @blur=${super.onBlur}
              @focus=${super.onFocus} />

            <span
              part="control"
              class=${clsx('input__control', `input__control--${this.size}`, {
                'input__control--checked': this.checked,
                'input__control--disabled': this.disabled,
                'input__control--indeterminate': this.indeterminate,
                'input__control--invalid': this.invalid,
              })}>
              <span
                part="high-contrast"
                class=${clsx(
                  'input__control-high-contrast',
                  `input__control-high-contrast--${this.size}`,
                  {
                    'input__control-high-contrast--disabled': this.disabled,
                  },
                )}></span>
              <slot name="iconcontent"> ${this.renderIcon()} </slot>
            </span>

            <div
              part="label-container"
              class=${clsx('choice__label-container', {
                'choice__label-container--disabled': this.disabled,
                'choice__label--bottom': this.descriptionPlacement === 'top',
                'choice__label--top': this.descriptionPlacement === 'bottom',
              })}>
              ${this.label &&
              html`<dap-ds-form-label
                id="label"
                class="label"
                part="label"
                .size=${this.size}
                .required=${this.required}
                .optional=${this.optional}
                .optionalLabel=${this.optionalLabel}
                .disabled=${this.disabled}
                label=${ifDefined(this.label)}
                subtle
                renderAs="span">
              </dap-ds-form-label>`}
              ${this.description &&
              html`
                <dap-ds-typography
                  class="description"
                  variant="description"
                  part="description"
                  size=${ifDefined(this.size as CommonFormElementSize)}
                  id="description">
                  ${this.description}
                </dap-ds-typography>
              `}
            </div>
          </div>
          ${this.feedback &&
          html`
            <dap-ds-feedback
              part="feedback"
              feedbackType=${ifDefined(this.feedbackType)}
              feedbackSize=${ifDefined(this.size) as CommonSize}
              id="feedback"
              .subtle=${this.subtle}>
              ${this.feedback}
            </dap-ds-feedback>
          `}
        </label>
      </div>
    `
  }
}
